import type {TFunction} from 'i18next';
import {Link2, Lock, RotateCw} from 'lucide-react';
import {VelaScene, type VelaContext, type VelaRule} from '../engine/types';
import {VelaOfferLink} from '../components/VelaChatLinks';

export const SHARE_LINK_CARD_ID = 'share-link';
export const CHAT_HOST_CARD_ID = 'chat-host';
export const INVITE_NEXT_GUEST_CARD_ID = 'invite-next-guest';

const hasPendingGuests = (ctx: VelaContext) => ctx.pendingGuests > 0;

function getWhatAnswerKey({isIVEnabled, biomatch}: VelaContext) {
  if (isIVEnabled && biomatch) return 'chat.hub.what.a_selfie';
  if (isIVEnabled) return 'chat.hub.what.a_photo';
  return 'chat.hub.what.a_form';
}

function getHoursSentence({stayTimes}: VelaContext, t: TFunction) {
  const {checkIn, checkOut} = stayTimes;
  if (checkIn && checkOut) return t('chat.hub.times.hours_both', {checkIn, checkOut});
  if (checkIn) return t('chat.hub.times.hours_check_in', {time: checkIn});
  if (checkOut) return t('chat.hub.times.hours_check_out', {time: checkOut});
  return null;
}

function getTimesVariant({stayOffers}: VelaContext) {
  const {earlyCheckInId, lateCheckOutId} = stayOffers;
  if (earlyCheckInId && lateCheckOutId) return 'both';
  if (earlyCheckInId) return 'early';
  if (lateCheckOutId) return 'late';
  return null;
}

const joinSentences = (...sentences: (string | null | false)[]) =>
  sentences.filter(Boolean).join(' ');

export const registrationRules: VelaRule[] = [
  {
    id: 'registration/ask-everyone-registered',
    section: 'askVela',
    scenes: [VelaScene.GuestRegistration],
    priority: 99,
    evaluate: (ctx, t) =>
      !hasPendingGuests(ctx) && ctx.totalGuests > 0
        ? {
            id: 'everyone-registered',
            title: t('chat.hub.everyone_registered.q'),
            description: t('chat.hub.everyone_registered.a', {count: ctx.totalGuests}),
          }
        : null,
  },
  {
    id: 'registration/ask-who',
    section: 'askVela',
    scenes: [VelaScene.GuestRegistration],
    priority: 90,
    evaluate: (ctx, t) =>
      hasPendingGuests(ctx)
        ? {
            id: 'ask-who',
            title: t('chat.hub.who.q'),
            description: joinSentences(
              t(ctx.guestCount.minors > 0 ? 'chat.hub.who.a_minors' : 'chat.hub.who.a', {
                count: ctx.totalGuests,
              }),
              t(ctx.canShareRegistration ? 'chat.hub.who.share' : 'chat.hub.who.add'),
            ),
          }
        : null,
  },
  {
    id: 'registration/ask-what',
    section: 'askVela',
    scenes: [VelaScene.GuestRegistration],
    priority: 80,
    evaluate: (ctx, t) =>
      hasPendingGuests(ctx)
        ? {
            id: 'ask-what',
            title: t('chat.hub.what.q'),
            description: t(getWhatAnswerKey(ctx)),
          }
        : null,
  },
  {
    id: 'registration/ask-times',
    section: 'askVela',
    scenes: [VelaScene.GuestRegistration, VelaScene.GuestRegistered],
    priority: 70,
    evaluate: (ctx, t) => {
      const variant = getTimesVariant(ctx);
      if (!variant) return null;

      const {earlyCheckInId, lateCheckOutId} = ctx.stayOffers;

      return {
        id: 'ask-times',
        title: t(`chat.hub.times.q_${variant}`),
        description: (
          <>
            {joinSentences(
              getHoursSentence(ctx, t),
              t(`chat.hub.times.offer_${variant}`),
            )}
            {earlyCheckInId && (
              <VelaOfferLink offerId={earlyCheckInId}>
                {t('chat.link.see_early_check_in')}
              </VelaOfferLink>
            )}
            {lateCheckOutId && (
              <VelaOfferLink offerId={lateCheckOutId}>
                {t('chat.link.see_late_check_out')}
              </VelaOfferLink>
            )}
          </>
        ),
      };
    },
  },
  {
    id: 'registration/ask-access',
    section: 'askVela',
    scenes: [VelaScene.GuestRegistration, VelaScene.GuestRegistered],
    priority: 60,
    evaluate: (ctx, t) =>
      ctx.isRemoteAccessEnabled
        ? {
            id: 'ask-access',
            title: t('chat.hub.access.q'),
            description: joinSentences(
              t('chat.hub.access.a'),
              Boolean(ctx.stayTimes.checkIn) &&
                t('chat.hub.access.from_time', {time: ctx.stayTimes.checkIn}),
              ctx.keysState === 'gated' && t('chat.hub.access.gated'),
            ),
          }
        : null,
  },
  {
    id: 'registration/ask-why',
    section: 'askVela',
    scenes: [VelaScene.GuestRegistration, VelaScene.GuestRegistered],
    priority: 50,
    evaluate: (_ctx, t) => ({
      id: 'ask-why',
      title: t('chat.hub.why.q'),
      description: t('chat.hub.why.a'),
    }),
  },
  {
    id: 'registration/share-link',
    section: 'quickActions',
    scenes: [VelaScene.IVUnavailable],
    priority: 20,
    evaluate: (_ctx, t) => ({
      id: SHARE_LINK_CARD_ID,
      icon: <Link2 />,
      title: t('card.share_link.title'),
      description: t('card.share_link.body'),
    }),
  },
  {
    id: 'registration/invite-next-guest',
    section: 'nextGuest',
    scenes: [VelaScene.GuestRegistered],
    priority: 100,
    evaluate: (ctx, t) =>
      hasPendingGuests(ctx) && ctx.canShareRegistration
        ? {
            id: INVITE_NEXT_GUEST_CARD_ID,
            title: t('card.invite_next_guest.title'),
            description: t('card.invite_next_guest.body'),
          }
        : null,
  },
  {
    id: 'registration/retry-usually-works',
    section: 'goodToKnow',
    scenes: [VelaScene.GuestRegistrationFailed],
    priority: 20,
    evaluate: (_ctx, t) => ({
      id: 'retry-usually-works',
      icon: <RotateCw />,
      title: t('card.retry_usually_works.title'),
      description: t('card.retry_usually_works.body'),
    }),
  },
  {
    id: 'registration/details-are-safe',
    section: 'goodToKnow',
    scenes: [VelaScene.GuestRegistrationFailed],
    priority: 10,
    evaluate: (_ctx, t) => ({
      id: 'details-are-safe',
      icon: <Lock />,
      tone: 'success',
      title: t('card.details_are_safe.title'),
      description: t('card.details_are_safe.body'),
    }),
  },
];
